import React, { useMemo } from "react";
import { Badge, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  getTravelStatusConfig,
  TravelStatusConfig,
  TRAVEL_STATUSES,
} from "@/config/travelStatuses";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TravelStatusBadgeProps {
  /** Travel status code: 100=In Progress, 200=Completed, 300=Submitted, etc. */
  status?: number | null | undefined;
  /** Size of the badge */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Whether to show the icon */
  showIcon?: boolean;
  /** Badge variant */
  variant?: "light" | "filled" | "outline" | "dot" | "default";
}

// ============================================
// Default Configuration
// ============================================

const DEFAULT_STATUS_CONFIG: TravelStatusConfig = {
  value: TRAVEL_STATUSES.IN_PROGRESS,
  icon: "QuestionMark",
  color: "gray",
  translationKey: "travels:statuses.unknown",
};

// ============================================
// Component
// ============================================

const TravelStatusBadgeComponent: React.FC<TravelStatusBadgeProps> = ({
  status,
  size = "md",
  showIcon = true,
  variant = "light",
}) => {
  const { t } = useTranslation(["travels"]);

  const config = useMemo(() => {
    return getTravelStatusConfig(status) || DEFAULT_STATUS_CONFIG;
  }, [status]);

  const label = useMemo(() => {
    return t(config.translationKey, { defaultValue: "Unknown" });
  }, [t, config.translationKey]);

  const iconSize = useMemo(() => {
    switch (size) {
      case "xs":
        return 12;
      case "sm":
        return 14;
      case "md":
        return 16;
      case "lg":
        return 18;
      case "xl":
        return 20;
      default:
        return 14;
    }
  }, [size]);

  return (
    <Badge color={config.color} variant={variant} size={size} radius="sm">
      {showIcon ? (
        <Group gap={4} wrap="nowrap" align="center">
          <AppIcon iconName={config.icon} size={iconSize} />
          <span>{label}</span>
        </Group>
      ) : (
        label
      )}
    </Badge>
  );
};

export const TravelStatusBadge = React.memo(TravelStatusBadgeComponent);